/**
 * Group jahit — dipisah jadi komponen sendiri 31 Agustus 2026 (K133).
 *
 * ⛔ Ia dipakai layar Personil yang bentuknya matriks tahap, dan tahap JAHIT
 * NOL punya kotak ketua/anggota di matriks itu: ia dilayani GROUP (K29b, K130),
 * dan penugasan personil ke sana nol berpengaruh apa pun.
 *
 * Menaruhnya di berkas terpisah supaya bagian ini nol ikut tumbuh setiap kali
 * matriksnya berubah — keduanya menjawab pertanyaan berbeda dan berubah karena
 * sebab yang berbeda.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  apiErrorMessage,
  buatGroupJahit,
  getGroupJahit,
  ubahGroupJahit,
} from "@/lib/papanKerjaApi";
import Hint from "@/components/etams/Hint";

function Lencana({ nada, children }) {
  const warna = {
    merah: "border-danger-soft-border bg-danger-soft text-danger-soft-foreground",
    kuning: "border-warning-soft-border bg-warning-soft text-warning-soft-foreground",
    hijau: "border-success-soft-border bg-success-soft text-success-soft-foreground",
    abu: "border-border bg-muted text-muted-foreground",
  }[nada];
  return (
    <span className={`whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium ${warna}`}>
      {children}
    </span>
  );
}

/**
 * Bagian GROUP JAHIT — terpisah dari penugasan personil, dan itu bukan pilihan
 * tata letak.
 *
 * ⛔ Penerima tugas JAHIT dibaca dari `sewing_groups`, BUKAN dari
 * `employee_production_stages` seperti lima tahap lain (K29b). Sampai
 * 31 Agustus 2026 layar ini menawarkan Jahit lewat jalur personil: penugasannya
 * tersimpan, peringatan "belum punya ketua" berhenti menyala, dan tugas Jahit
 * tetap dikirim ke ketua sewing group — atau nol terkirim sama sekali kalau
 * group-nya belum ada.
 *
 * ⚠ Kendali mati yang membuat lampu jadi HIJAU lebih buruk daripada nol ada
 * kendalinya. Yang kedua membuat orang bertanya; yang pertama membuat orang
 * berhenti bertanya.
 */
/*
 * T33 (Titipan auditor 26 Okt 2026): group jahit IKUT pilihan wilayah layar Personil. Dulu daftar dimuat tanpa
 * wilayah (Owner melihat group Surabaya saat Samarinda dipilih) dan group baru bisa tersimpan di wilayah lain.
 */
export default function GroupJahit({ orang, regionId, onBerubah, setGalat }) {
  const [data, setData] = useState(null);
  const [muat, setMuat] = useState(true);
  const [buka, setBuka] = useState(null);
  const [sibuk, setSibuk] = useState(false);
  const [baru, setBaru] = useState(null);

  const muatData = useCallback(async () => {
    setMuat(true);
    try {
      const { data: d } = await getGroupJahit(regionId ? { region_id: regionId } : undefined);
      setData(d);
    } catch (e) {
      setGalat(apiErrorMessage(e));
    } finally {
      setMuat(false);
    }
  }, [setGalat, regionId]);

  useEffect(() => {
    muatData();
  }, [muatData]);

  const simpan = async (fn) => {
    setSibuk(true);
    setGalat("");
    try {
      await fn();
      setBuka(null);
      setBaru(null);
      await muatData();
      onBerubah?.();
    } catch (e) {
      setGalat(apiErrorMessage(e));
    } finally {
      setSibuk(false);
    }
  };

  const pilihOrang = (nilai, ubah) => (
    <select
      className="rounded border border-border px-2 py-1 text-sm"
      value={nilai || ""}
      onChange={(e) => ubah(e.target.value || null)}
    >
      <option value="">—</option>
      {orang.map((o) => (
        <option key={o.id} value={o.id}>{o.full_name}</option>
      ))}
    </select>
  );

  if (muat) return <p className="text-sm text-muted-foreground">Memuat group jahit…</p>;
  if (!data) return null;

  const penuh = data.sisa_kuota === 0;

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="font-semibold text-foreground">
            Group Jahit{" "}
            <Hint>Tahap Jahit dikerjakan group, bukan perorangan — jadi diatur di sini, tidak lewat daftar tahap di bawah.</Hint>
          </h2>
        </div>
        {/* ⚠ Kuota ditampilkan SEBELUM orang mengetik, bukan ditolak sesudah. */}
        <span className="text-sm text-muted-foreground tabular-nums">
          {data.aktif} dari {data.maks} terpakai
        </span>
      </div>

      <div className="mt-3 space-y-2">
        {data.items.length === 0 && (
          <p className="rounded-lg border border-warning-soft-border bg-warning-soft px-3 py-2 text-sm text-warning-soft-foreground">
            Belum ada group jahit di wilayah ini — tugas tahap Jahit tidak akan
            terkirim ke siapa pun.
          </p>
        )}
        {data.items.map((g) => (
          <div key={g.id} className="rounded-lg border border-border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-foreground">
                  {g.name} <span className="text-muted-foreground">·</span>{" "}
                  <span className="font-mono text-sm text-muted-foreground">{g.code}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Ketua: {g.ketua_nama || "—"}
                  {" · "}
                  Anggota: {g.anggota_nama || "—"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {!g.is_active && <Lencana nada="abu">Nonaktif</Lencana>}
                {/* ⛔ Group tanpa anggota BERJALAN, tapi tugasnya nol punya
                    cadangan saat ketuanya libur (K129). Itu wajib terlihat. */}
                {g.tanpa_cadangan && g.is_active && (
                  <Lencana nada="kuning">Tanpa cadangan</Lencana>
                )}
                <button
                  type="button"
                  onClick={() => setBuka(buka === g.id ? null : g.id)}
                  className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
                >
                  {buka === g.id ? "Tutup" : "Ubah"}
                </button>
              </div>
            </div>

            {buka === g.id && (
              <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg bg-muted p-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">Ketua</span>
                  {pilihOrang(g.leader_employee_id, (v) =>
                    simpan(() => ubahGroupJahit(g.id, { leader_employee_id: v })))}
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-muted-foreground">Anggota (cadangan)</span>
                  {pilihOrang(g.member_employee_id, (v) =>
                    simpan(() => ubahGroupJahit(g.id,
                      v ? { member_employee_id: v } : { kosongkan_anggota: true })))}
                </label>
                <button
                  type="button"
                  disabled={sibuk}
                  onClick={() => simpan(() => ubahGroupJahit(g.id, { is_active: !g.is_active }))}
                  className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
                >
                  {g.is_active ? "Nonaktifkan" : "Aktifkan"}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {baru ? (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-muted p-3">
          {orang.length === 0 && (
            <p className="w-full text-xs text-warning-soft-foreground" data-testid="group-jahit-tanpa-orang">
              Belum ada karyawan di wilayah ini untuk dipilih sebagai ketua/anggota. Tambahkan dulu di HRD › Karyawan,
              lalu buka lagi formulir ini.
            </p>
          )}
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Kode</span>
            <input
              className="w-32 rounded border border-border px-2 py-1 font-mono text-sm uppercase"
              value={baru.code}
              onChange={(e) => setBaru({ ...baru, code: e.target.value.toUpperCase() })}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Nama</span>
            <input
              className="w-48 rounded border border-border px-2 py-1 text-sm"
              value={baru.name}
              onChange={(e) => setBaru({ ...baru, name: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Ketua</span>
            {pilihOrang(baru.leader_employee_id, (v) =>
              setBaru({ ...baru, leader_employee_id: v }))}
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Anggota</span>
            {pilihOrang(baru.member_employee_id, (v) =>
              setBaru({ ...baru, member_employee_id: v }))}
          </label>
          <button
            type="button"
            disabled={sibuk || !baru.code || !baru.name || !baru.leader_employee_id}
            onClick={() => simpan(() => buatGroupJahit({ ...baru, region_id: regionId || undefined }))}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            Simpan group
          </button>
          <button
            type="button"
            onClick={() => setBaru(null)}
            className="rounded-lg border border-border px-3 py-2 text-sm text-foreground"
          >
            Batal
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={penuh}
          title={penuh ? `Batasnya ${data.maks} group aktif per wilayah` : undefined}
          onClick={() => setBaru({ code: "", name: "", leader_employee_id: null, member_employee_id: null })}
          className="mt-3 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
        >
          {penuh ? `Kuota penuh (${data.maks} group)` : "Tambah group jahit"}
        </button>
      )}
    </div>
  );
}
